import { API_BASE_URL } from '../config';
import api from './axiosInstance';

/**
 * Obtiene servicios paginados del backend
 * @param {number} page - Número de página (comienza en 1)
 * @param {number} pageSize - Cantidad de registros por página
 * @param {boolean} onlyEnabled - Si true, solo servicios activos
 * @param {string} search - Término de búsqueda (opcional)
 * @param {number|null} businessLineId - ID de línea de negocio (opcional)
 * @returns {Promise} - { total, services }
 */
export async function getServicesPaged(
  page = 1,
  pageSize = 10,
  status = 'Activos',
  search = null,
  sortBy = null,
  desc = null,
  businessLineId = null
) {
  try {
    const params = {
      page,
      pageSize,
    };

    // status: 'Todos' | 'Activos' | 'Inactivos' or boolean for isEnabled
    if (typeof status === 'boolean') {
      params.isEnabled = status;
    } else if (status && status !== 'Todos') {
      params.status = status;
    }

    // Solo agregar search si tiene valor
    if (search && search.trim()) {
      params.search = search.trim();
    }

    if (sortBy) {
      params.sortBy = sortBy;
    }

    if (desc !== null && desc !== undefined) {
      params.desc = desc;
    }

    if (businessLineId !== null && businessLineId !== undefined) {
      params.businessLineId = businessLineId;
    }

    const response = await api.get(`${API_BASE_URL}/Services/Paged`, {
      headers: {
        'Content-Type': 'application/json',
      },
      params,
    });

    return {
      total: response.data.total || 0,
      services: response.data.items || [],
    };
  } catch (error) {
    console.error('getServicesPaged failed:', error);
    console.error('Error details:', error.response?.data);
    error.customMessage = 'No se pudo obtener el listado de servicios.';
    throw error;
  }
}

/**
 * Obtiene un servicio por ID
 * @param {number} serviceId - ID del servicio
 * @returns {Promise} - El servicio
 */
export async function getServiceById(serviceId) {
  try {
    const response = await api.get(`${API_BASE_URL}/Services/${serviceId}`, {
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return response.data;
  } catch (error) {
    console.error('getServiceById failed:', error);
    console.error('Error details:', error.response?.data);
    error.customMessage = 'No se pudo obtener el servicio.';
    throw error;
  }
}

/**
 * Obtiene servicios por línea de negocio
 * @param {number} businessLineId - ID de la línea de negocio
 * @returns {Promise} - Lista de servicios
 */
export async function getServicesByBusinessLine(businessLineId) {
  try {
    const response = await api.get(
      `${API_BASE_URL}/Services/ByBusinessLine/${businessLineId}`,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    return response.data || [];
  } catch (error) {
    console.error('getServicesByBusinessLine failed:', error);
    console.error('Error details:', error.response?.data);
    error.customMessage =
      'No se pudo obtener los servicios por línea de negocio.';
    throw error;
  }
}

/**
 * Crea un nuevo servicio
 * @param {object} serviceData - Datos del servicio { name, businessLineId }
 * @returns {Promise} - El servicio creado
 */
export async function createService(serviceData) {
  try {
    const payload = {
      name: serviceData.name || '',
      businessLineId: serviceData.businessLineId,
    };

    const response = await api.post(`${API_BASE_URL}/Services`, payload, {
      headers: { 'Content-Type': 'application/json' },
    });

    return response.data;
  } catch (error) {
    console.error('createService failed:', error);
    console.error('Error details:', error.response?.data);
    error.customMessage = 'No se pudo crear el servicio.';
    throw error;
  }
}

/**
 * Actualiza un servicio existente
 * @param {number} serviceId - ID del servicio
 * @param {object} serviceData - Datos actualizados { name, businessLineId, isEnabled }
 * @returns {Promise} - El servicio actualizado
 */
export async function updateService(serviceId, serviceData) {
  try {
    const payload = {
      name: serviceData.name || '',
      businessLineId: serviceData.businessLineId,
      isEnabled: serviceData.isEnabled ?? true,
    };

    const response = await api.put(
      `${API_BASE_URL}/Services/${serviceId}`,
      payload,
      {
        headers: { 'Content-Type': 'application/json' },
      }
    );

    return response.data;
  } catch (error) {
    console.error('updateService failed:', error);
    console.error('Error details:', error.response?.data);
    error.customMessage = 'No se pudo actualizar el servicio.';
    throw error;
  }
}

/**
 * Elimina un servicio (borrado lógico)
 * @param {number} serviceId - ID del servicio a eliminar
 * @returns {Promise}
 */
export async function deleteService(serviceId) {
  try {
    const response = await api.delete(`${API_BASE_URL}/Services/${serviceId}`, {
      headers: { 'Content-Type': 'application/json' },
    });

    return response.data;
  } catch (error) {
    console.error('deleteService failed:', error);
    console.error('Error details:', error.response?.data);
    error.customMessage = 'No se pudo eliminar el servicio.';
    throw error;
  }
}
